import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SuttonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Sutton Pothole Claims | Double-RED DfT Scorecard | Fixtyer"
        description="Sutton Council earns RED on both road condition and best practice — the weakest London batch scorecard — kept AMBER overall only by GREEN spend. 326km of U-roads, just two years of published condition data. See what this means for your pothole claim."
        canonical="/councils/sutton"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Sutton', url: '/councils/sutton' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Sutton spends over three times its DfT allocation — can I still claim?',
            answer: 'Yes. Sutton earns a GREEN Spend scorecard because it invests well beyond its DfT capital allocation — projected at £2.44m against £750,000 in 2025/26. But the DfT Overall rating is AMBER because both Condition and Best Practice are RED. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What does Sutton\'s RED Best Practice rating mean for my claim?',
            answer: 'Sutton\'s transparency report publishes only two years of road condition data (2023 and 2024) when the DfT framework expects five. The council only started using its RoadAI survey tool in 2023, and its cycle-route gazetteers are acknowledged as incomplete. A RED Best Practice scorecard signals gaps in the asset-management evidence Sutton can produce — which weakens a blanket Section 58 defence if you can show the specific defect was missed.',
          },
          {
            question: 'What if my pothole was on a residential street in Sutton?',
            answer: 'Unclassified roads make up 326km — 80% of Sutton\'s 406.67km road network. The council uses RoadAI by Vaisala to support officer visual assessments rather than SCANNER surveys on this network. Over 85% of borough roads were surveyed in 2023 and 2024, but with only two published years of condition data, establishing what the council knew about your specific U-road at the time of your incident requires prior reports and photos.',
          },
          {
            question: 'Sutton\'s RED percentage fell from 10.61% to 7.38% — does that weaken my claim?',
            answer: 'Not necessarily — but context matters. The 10.61% figure is from 2023, the first year Sutton fully used RoadAI. Amber-condition roads rose from 30.53% to 33.04% in the same period, meaning more carriageways are edging toward maintenance. The DfT still rates Sutton RED on condition overall. Your claim should focus on the specific defect and road, not borough-wide percentages.',
          },
          {
            question: 'Does Sutton\'s Section 58 streetworks admission help my claim?',
            answer: 'It can. Sutton states that using the nationally recognised DfT Streetworks system, it "can identify issues such Section 58s whilst coordinating work activities amongst statutory undertakers and highways teams alike." That is an admission the council has systems to track statutory liability — which raises the question of why your specific defect was not identified and repaired before it caused damage.',
          },
          {
            question: 'Sutton repaired 5,123 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Sutton filled an estimated 5,123 potholes in 2024/25, up from 3,544 in 2022/23. The council also resurfaced approximately 8.05km of road and reconstructed around 2km of footway in the most recent financial year — on a 406.67km network where 80% is unclassified. Reactive repairs and limited resurfacing do not eliminate liability for individual defects that formed and persisted between inspections.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Sutton Council"
        councilSlug="sutton"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/sutton"
        headline="Sutton Council Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Sutton</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Sutton: RED on Condition and Best Practice Both
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Sutton is the weakest scorecard in the London batch — <strong className="text-rose-400">RED</strong> on
            both road condition and best practice, kept off an overall RED only by a
            <strong className="text-emerald-400"> GREEN</strong> spend rating. On a network where
            <strong className="text-rose-400"> 326km — 80% of all roads — are unclassified</strong>, the council
            has published just two years of condition data and only started its RoadAI survey programme in 2023.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">326km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 80% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              Sutton maintains just 80.67km of A, B and C roads against 326km of U-roads. The council
              assesses these largely through RoadAI-assisted visual surveys — not the SCANNER laser
              technology used on classified roads nationally.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Two years of published condition data from Sutton's own transparency report — the DfT
            expects five
          </p>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">Borough-wide condition (all road classes combined)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    <th className="px-4 py-2 text-center font-semibold text-brand-900">Roads surveyed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10.61%</td>
                    <td className="px-4 py-2 text-center">30.53%</td>
                    <td className="px-4 py-2 text-center">58.77%</td>
                    <td className="px-4 py-2 text-center">1,140</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.38%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">33.04%</td>
                    <td className="px-4 py-2 text-center font-bold">59.58%</td>
                    <td className="px-4 py-2 text-center font-bold">1,138</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              RED-condition roads fell from 10.61% to 7.38%, but amber-condition roads rose from 30.53% to
              33.04% — more of the network edging toward maintenance. The DfT still rates Sutton
              <strong> RED on condition</strong>. Sutton does not publish condition data broken down by A, B, C
              and U road class.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">How Sutton Defines Condition</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>Green</strong> — No further investigation or treatment required (above 67%)</li>
                <li>• <strong>Amber</strong> — Maintenance may be required soon (between 34% and 67%)</li>
                <li>• <strong>Red</strong> — Should be considered for maintenance (below 33%)</li>
              </ul>
              <p className="text-surface-600 text-sm mt-3">
                From 2026/27 a new methodology based on BSI PAS2161 will replace this three-category
                system with five categories.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Two-Year Data Gap</h3>
              <p className="text-amber-800 text-sm">
                The DfT transparency framework expects five years of condition data. Sutton's report
                publishes only 2023 and 2024 — both years after the council adopted RoadAI. There is no
                published baseline for 2020/21 through 2022/23, which is a key reason the DfT Best Practice
                scorecard is RED.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The Council only started using the RoadAI package to fully help with Officer Assessment, during 2023. During 2023 and 2024, over 85% of the borough's roads were surveyed as part of the annual assessments."
              source="Sutton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — But Condition Still RED</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£750k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£2.44m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">71.3%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Sutton spends over <span className="text-emerald-400">three times its DfT allocation</span> on
              capital works — and preventative maintenance has risen from 56.1% to 71.3% since 2021/22. That
              earns GREEN on spend. It has not earned GREEN on condition or best practice.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Borough */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 326km U-Road Borough
          </h2>
          <p className="text-surface-600 mb-8">
            80% of Sutton's carriageway network is unclassified — assessed differently from classified roads
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">33.31</td>
                  <td className="px-6 py-4 text-center">8.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">47.36</td>
                  <td className="px-6 py-4 text-center">11.6%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-bold text-brand-900">U roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-bold font-mono text-rose-600">326</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">80.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-bold font-mono">406.67</td>
                  <td className="px-6 py-4 text-center">100%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center font-mono">453.92</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-brand-900">Cycleways</td>
                  <td className="px-6 py-4 text-center font-mono">50.46</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">SCANNER vs RoadAI</h3>
              <p className="text-surface-600 text-sm mb-3">
                Road condition assessments on classified roads in England are made predominantly using
                SCANNER laser-based technology. Sutton states that at a local level — with its large number
                of unclassified roads — it uses RoadAI by Vaisala to support officer visual assessments.
              </p>
              <p className="text-surface-600 text-sm">
                RoadAI provides a third-party independent score which the council uses to assist with
                compiling its annual maintenance programmes. This is a different methodology from SCANNER,
                and Sutton only adopted it fully in 2023.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Incomplete Asset Records</h3>
              <p className="text-amber-800 text-sm mb-3">
                Sutton acknowledges its cycle route gazetteers do not have a complete picture of all cycle
                routes, with the exception of national cycle routes. Regional (TfL) and local cycle route
                data are incomplete.
              </p>
              <p className="text-amber-800 text-sm">
                Gaps in asset records are part of why the DfT rates Best Practice RED — a council cannot
                claim comprehensive network knowledge when its own data is incomplete.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Sutton's overwhelmingly U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road among the 85%+ surveyed in 2023 or 2024 — or the remainder?</li>
              <li>• If RoadAI only started in 2023, what condition records exist for your road before that?</li>
              <li>• With 33% of the network in amber condition, was your road flagged for maintenance?</li>
              <li>• Does the council's incomplete cycle and asset data undermine its knowledge of your route?</li>
            </ul>
            <p className="text-white font-medium">
              A double-RED scorecard on condition and best practice is not the profile of a council with
              bulletproof inspection records.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Spending Well Beyond DfT Allocation
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of highways maintenance spending from Sutton's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£1,238</td>
                  <td className="px-4 py-3 text-center">£969</td>
                  <td className="px-4 py-3 text-center">56.1%</td>
                  <td className="px-4 py-3 text-center">43.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£1,538</td>
                  <td className="px-4 py-3 text-center">£1,337</td>
                  <td className="px-4 py-3 text-center">53.5%</td>
                  <td className="px-4 py-3 text-center">46.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£222</td>
                  <td className="px-4 py-3 text-center">£2,580</td>
                  <td className="px-4 py-3 text-center">£1,550</td>
                  <td className="px-4 py-3 text-center">64.4%</td>
                  <td className="px-4 py-3 text-center">35.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£460</td>
                  <td className="px-4 py-3 text-center">£2,453</td>
                  <td className="px-4 py-3 text-center">£1,270</td>
                  <td className="px-4 py-3 text-center">69.6%</td>
                  <td className="px-4 py-3 text-center">30.4%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">£750</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£2,443</td>
                  <td className="px-4 py-3 text-center font-bold">£1,284</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">71.3%</td>
                  <td className="px-4 py-3 text-center font-bold">28.7%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Tasks that this would have been allocated to include, Planned Maintenance (Resurfacing and Footway reconstruction), Reactive Maintenance will undertake immediate repairs to the Highways network in line with our Section 41 and Section 58 statutory undertakings, we also undertake proactive repairs to ensure we maintain a safe highways for all users. In the most recent financial year, the Council's Planned Maintenance section resurfaced approximately 8.05km of road, and reconstructed around 2km of footway."
              source="Sutton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">8.05km Resurfaced on 406.67km</h3>
              <p className="text-surface-600 text-sm">
                Sutton resurfaced approximately 8.05km of road in the most recent financial year — roughly
                2% of its total road network. On a borough where 80% of roads are unclassified residential
                streets, the gap between aggregate spend and your specific carriageway is wide.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Preventative Share Rising</h3>
              <p className="text-emerald-800 text-sm">
                Estimated preventative maintenance has risen from 56.1% in 2021/22 to a projected 71.3% in
                2025/26. That is genuine progress — but preventative spend on other roads does not
                automatically prove your specific defect was reasonably managed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole Repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Pothole Repairs Rising — Roads Still Deteriorating
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled per year from Sutton's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">4,350</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,544</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">4,990</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5,123</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,007</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Repairs Up 45% Since 2022/23</h3>
              <p className="text-surface-600 text-sm">
                Sutton filled an estimated 5,123 potholes in 2024/25 — up from 3,544 in 2022/23, a rise of
                roughly 45%. A network producing potholes at an accelerating rate is one where defects
                routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Table Typo Note</h3>
              <p className="text-amber-800 text-sm">
                Sutton's published potholes table includes a row labelled "2022/22" with 3,544 repairs —
                the same figure as 2022/23. This appears to be a typographical error in the council's report.
                The four-year total above uses 2021/22 through 2024/25 without double-counting.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Elastomac — New Reactive Approach</h3>
            <p className="text-brand-200 mb-4">
              Sutton has rolled out Elastomac surface dressing for reactive maintenance, trialled initially in
              Kingston and now applied across the borough. As of June 2025, the council had resurfaced
              1,779m² using Elastomac.
            </p>
            <QuoteCard
              quote="We are pleased to confirm that we have moved beyond the trial phase and are now applying this across the borough."
              source="Sutton Council Local Highways Maintenance Transparency Report 2025"
            />
            <p className="text-brand-300 text-sm mt-4">
              New repair technology does not retroactively fix defects that formed and persisted before
              treatment — and 1,779m² across a 406.67km network is a fraction of total carriageway.
            </p>
          </div>
        </div>
      </section>

      {/* Best Practice RED */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why Best Practice Is RED
          </h2>
          <p className="text-surface-600 mb-8">
            What Sutton's own report reveals about asset management gaps
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The cycle route information might not currently fully reflect what's on the ground. Currently, our gazetteers don't have a complete picture of all cycle routes, with the exception of national cycle routes. Regional (TfL) and local cycle route data are incomplete at this time but this is something the borough is working towards so this caveat would apply."
              source="Sutton Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="From 2026/27 a new methodology will be used based on the BSI PAS2161 standard. Local Highway Authorities will be required to use a supplier that has been accredited against PAS2161. This new standard will categorise roads into five categories instead of three to help government gain a more detailed understanding of road conditions in England."
              source="Sutton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Two Years, Not Five</h3>
              <p className="text-rose-800 text-sm">
                Only 2023 and 2024 condition data published. No historical baseline for comparison or trend
                analysis across the full DfT reporting period.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">RoadAI From 2023 Only</h3>
              <p className="text-rose-800 text-sm">
                Survey methodology changed in 2023 when RoadAI was adopted. Pre-2023 condition records
                for U-roads rely on officer visual assessment without the third-party scoring tool.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Incomplete Gazetteers</h3>
              <p className="text-rose-800 text-sm">
                Cycle route data incomplete except national routes. TfL and local cycle route records not
                fully reflected in the council's asset database.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="Using the Nationally recognised DfT Streetworks system, we can identify issues such Section 58s whilst coordinating work activities amongst statutory undertakers and highways teams alike."
              source="Sutton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against Sutton's Double-RED Scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: GREEN spend helps the council — but double RED opens doors for specific claims
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — invests well beyond DfT capital allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative maintenance share rising to 71.3% (projected 2025/26)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Over 85% of borough roads surveyed in 2023 and 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Elastomac rollout and planned maintenance programme published online
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> RED-condition percentage fell from 10.61% to 7.38% in published data
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect the council to point to spend and survey coverage. Generic claims will struggle on
                A-roads; U-roads are a different story.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What Works In Yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED condition — weakest London batch condition scorecard
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — only two years of condition data, incomplete asset records
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 326km of U-roads (80%) assessed via RoadAI, not SCANNER
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Amber-condition roads rising to 33.04% — maintenance backlog growing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 5,123 potholes filled in 2024/25 — defects forming faster than full resurfacing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Just 8.05km resurfaced in the most recent year on a 406.67km network
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but double-RED on condition and best practice, your claim
              lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, Sutton's RoadAI-only-since-2023 gap is your strongest structural argument</li>
              <li>• Whether your road was in the 85% surveyed or the remainder without a published condition record</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Sutton's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Sutton?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            London's weakest scorecard demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No double-RED scorecard cited</li>
                <li>• No RoadAI survey-gap argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Double-RED condition and best practice documented</li>
                <li>✅ 326km U-road network and RoadAI gap argued</li>
                <li>✅ 18,007 pothole repairs over four years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Sutton</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=sutton"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Sutton Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sutton spends over three times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Sutton earns a GREEN Spend scorecard because it invests well beyond its DfT capital
                allocation — projected at £2.44m against £750,000 in 2025/26. But the DfT Overall rating is
                AMBER because both Condition and Best Practice are RED. Section 58 turns on whether the
                specific defect that damaged your vehicle was reasonably inspected and repaired, not on
                aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does Sutton's RED Best Practice rating mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Sutton's transparency report publishes only two years of road condition data (2023 and 2024)
                when the DfT framework expects five. The council only started using its RoadAI survey tool
                in 2023, and its cycle-route gazetteers are acknowledged as incomplete. A RED Best Practice
                scorecard signals gaps in the asset-management evidence Sutton can produce — which weakens a
                blanket Section 58 defence if you can show the specific defect was missed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential street in Sutton?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 326km — 80% of Sutton's 406.67km road network. The council uses
                RoadAI by Vaisala to support officer visual assessments rather than SCANNER surveys on this
                network. Over 85% of borough roads were surveyed in 2023 and 2024, but with only two
                published years of condition data, establishing what the council knew about your specific
                U-road at the time of your incident requires prior reports and photos.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sutton's RED percentage fell from 10.61% to 7.38% — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily — but context matters. The 10.61% figure is from 2023, the first year Sutton
                fully used RoadAI. Amber-condition roads rose from 30.53% to 33.04% in the same period,
                meaning more carriageways are edging toward maintenance. The DfT still rates Sutton RED on
                condition overall. Your claim should focus on the specific defect and road, not borough-wide
                percentages.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Sutton's Section 58 streetworks admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Sutton states that using the nationally recognised DfT Streetworks system, it "can
                identify issues such Section 58s whilst coordinating work activities amongst statutory
                undertakers and highways teams alike." That is an admission the council has systems to track
                statutory liability — which raises the question of why your specific defect was not identified
                and repaired before it caused damage.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sutton repaired 5,123 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Sutton filled an estimated 5,123 potholes in 2024/25, up from 3,544 in 2022/23. The
                council also resurfaced approximately 8.05km of road and reconstructed around 2km of footway in
                the most recent financial year — on a 406.67km network where 80% is unclassified. Reactive
                repairs and limited resurfacing do not eliminate liability for individual defects that formed
                and persisted between inspections.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.sutton.gov.uk/streets-transport-and-parking/streets-roads-and-highways/road-resurfacing/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Sutton Council Local Highways Maintenance Transparency Report (2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
